<template>
  <a-dialog
    v-model="dialog"
    :max-width="options.width"
    :style="{ zIndex: options.zIndex }"
    @keydown.esc="cancel"
    persistent
    :hide-overlay="hideOverlay"
    :scrollable="options.sendEmailButtons"
  >
    <v-card>
      <v-toolbar :color="options.color" dense>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-toolbar-title v-on="on" v-bind="attrs" style="font-size: 1rem">{{
              title
            }}</v-toolbar-title>
          </template>
          <span>
            {{ title }}
          </span>
        </v-tooltip>
        <v-spacer></v-spacer>
        <v-btn icon large>
          <v-icon>mdi-{{ options.dialogIcon }}</v-icon>
        </v-btn>
      </v-toolbar>
      
      <!-- Display name section -->
      <div v-if="hasTimer" class="display-name-section">
        <span class="display-name-text">{{ userName }}</span>
      </div>
      
      <v-card-text v-show="!!message" class="black--text">
        <span v-html="$sanitizeHtml(message)"></span>
        <slot name="checkbox"></slot>
        
        <!-- Timer display - controlled by hasTimer prop -->
        <div v-if="hasTimer" class="timer-container">
          <div class="timer-content">
            <div class="timer-text">{{ $t("expiringIn") }} {{ formattedTime }}</div>
          </div>
          <v-progress-linear
            :value="progressValue"
            height="6"
            :color="timerColor"
            striped
          ></v-progress-linear>
        </div>
      </v-card-text>
      <v-divider v-if="options.sendEmailButtons"></v-divider>
      <v-card-actions class="justify-center">
        <v-tooltip bottom v-if="!options.removeAgreeBtn">
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              v-on="on"
              v-bind="attrs"
              ref="primaryButton"
              color="primaryDark"
              class="font-weight-bold mr-2"
              small
              @click.native="agree"
              v-if="!options.removeAgreeBtn"
              :disabled="timerExpired && hasTimer"
            >
              <span v-if="options.yesNoButtons">{{ $t("btnYes") }}</span>
              <span v-else-if="options.sendEmailButtons">{{
                $t("btnSendEmail")
              }}</span>
              <span v-else>{{ options.btnOk }}</span>
            </a-btn>
          </template>
          <span v-if="options.yesNoButtons">{{ $t("btnYes") }}</span>
          <span v-else-if="options.sendEmailButtons">{{
            $t("btnSendEmail")
          }}</span>
          <span v-else>{{ options.btnOk }}</span>
        </v-tooltip>
        <v-tooltip bottom v-if="!options.noconfirm">
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              v-if="!options.noconfirm"
              v-on="on"
              v-bind="attrs"
              small
              color="secondary"
              class="font-weight-bold"
              @click.native="cancel"
              ><span v-if="options.yesNoButtons">{{ $t("btnNo") }}</span>
              <span v-else-if="options.sendEmailButtons">{{
                $t("btnDontSendEmail")
              }}</span>
              <span v-else>{{ btnCancel }}</span>
            </a-btn>
          </template>
          <span v-if="options.yesNoButtons">{{ $t("btnNo") }}</span>
          <span v-else-if="options.sendEmailButtons">{{
            $t("btnDontSendEmail")
          }}</span>
          <span v-else>{{ btnCancel }}</span>
        </v-tooltip>
      </v-card-actions>
    </v-card>
  </a-dialog>
</template>

<script>
import ADialog from "../wrapper-component/ADialog.vue";
import ABtn from "../wrapper-component/ABtn.vue";
import { RESET_MFA_INITIAL_TIMEOUT } from "../utils/constant";

export default {
  name: "ConfirmDialog",
  components: {
    ADialog,
    ABtn,
  },
  props: {
    hideOverlay: {
      type: Boolean,
      default: false,
    },
    hasTimer: {
      type: Boolean,
      default: false,
    },
    userName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      dialog: false,
      resolve: null,
      reject: null,
      message: null,
      title: null,
      timeLeft: RESET_MFA_INITIAL_TIMEOUT,
      timerInterval: null,
      timerExpired: false,
      startTime: null,
      endTime: null,
      options: {
        color: "primaryLight1",
        width: 400,
        zIndex: 200,
        removeAgreeBtn: false,
        noconfirm: false,
        yesNoButtons: false,
        sendEmailButtons: false,
        dialogIcon: "comment-question",
        btnOk: this.$t("btnOk"),
        btnCancel: this.$t("btnLabelCancel"),
      },
    };
  },
  computed: {
    btnCancel() {
      return this.$t("btnLabelCancel");
    },
    formattedTime() {
      const minutes = Math.floor(this.timeLeft / 60);
      const seconds = this.timeLeft % 60;
      return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    },
    progressValue() {
      return (this.timeLeft / RESET_MFA_INITIAL_TIMEOUT) * 100;
    },
    timerColor() {
      if (this.timeLeft > 60) return "blue";
      if (this.timeLeft > 30) return "orange";
      return "red";
    }
  },
  methods: {
    open(title, message, options = {}) {
      this.dialog = true;
      this.title = title;
      this.message = message;
      this.options = Object.assign({}, this.options, options);
      
      // Check if timer should be enabled (from options or prop)
      const shouldUseTimer = options.hasTimer || this.hasTimer;
      
      if (shouldUseTimer) {
        this.initializeTimer();
      }
      
      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    
    initializeTimer() {
      this.startTime = performance.now();
      this.endTime = this.startTime + (RESET_MFA_INITIAL_TIMEOUT * 1000); // Convert to milliseconds
      this.timeLeft = RESET_MFA_INITIAL_TIMEOUT;
      this.timerExpired = false;
      this.startTimer();
    },
    
    startTimer() {
      // Clear any existing timer
      this.clearTimer();
      
      const updateTimer = () => {
        if (!this.startTime || !this.endTime) {
          return;
        }
        
        const now = performance.now();
        const remainingMs = Math.max(0, this.endTime - now);
        const remainingSeconds = Math.ceil(remainingMs / 1000);
        
        this.timeLeft = remainingSeconds;
        
        if (remainingMs <= 0) {
          this.timerExpired = true;
          this.clearTimer();
          // Auto-close the dialog when timer reaches 0
          this.$nextTick(() => {
            this.cancel();
          });
        } else {
          this.timerInterval = requestAnimationFrame(updateTimer);
        }
      };
      
      this.timerInterval = requestAnimationFrame(updateTimer);
      
      this.backupInterval = setInterval(() => {
        if (this.startTime && this.endTime) {
          const now = performance.now();
          const remainingMs = Math.max(0, this.endTime - now);
          const remainingSeconds = Math.ceil(remainingMs / 1000);
          
          if (Math.abs(this.timeLeft - remainingSeconds) > 1) {
            this.timeLeft = remainingSeconds;
          }
          
          if (remainingMs <= 0 && !this.timerExpired) {
            this.timerExpired = true;
            this.clearTimer();
            this.$nextTick(() => {
              this.cancel();
            });
          }
        }
      }, 1000);
    },
    clearTimer() {
      if (this.timerInterval) {
        cancelAnimationFrame(this.timerInterval);
        this.timerInterval = null;
      }
      
      if (this.backupInterval) {
        clearInterval(this.backupInterval);
        this.backupInterval = null;
      }
    },
    
    agree() {
      this.clearTimer();
      this.$emit("ok");
      this.resolve(true);
      this.dialog = false;
    },
    cancel() {
      this.clearTimer();
      this.$emit("cancel");
      this.resolve(false);
      this.dialog = false;
    },
    
    handleVisibilityChange() {
      if (document.hidden) {
        if (this.startTime && this.endTime) {
          const now = performance.now();
          const remainingMs = Math.max(0, this.endTime - now);
          this.timeLeft = Math.ceil(remainingMs / 1000);
        }
      } else {
        if (this.startTime && this.endTime) {
          const now = performance.now();
          const remainingMs = Math.max(0, this.endTime - now);
          const remainingSeconds = Math.ceil(remainingMs / 1000);
          
          this.timeLeft = remainingSeconds;
          
          if (remainingMs <= 0 && !this.timerExpired) {
            this.timerExpired = true;
            this.clearTimer();
            this.$nextTick(() => {
              this.cancel();
            });
          }
        }
      }
    }
  },
  
  mounted() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);   
    window.addEventListener('focus', this.handleVisibilityChange);
    window.addEventListener('blur', this.handleVisibilityChange);
  },
  
  beforeDestroy() {
    this.clearTimer();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleVisibilityChange);
    window.removeEventListener('blur', this.handleVisibilityChange);
  },
  
  watch: {
    dialog(newVal) {
      if (!newVal) {
        this.clearTimer();
        this.startTime = null;
        this.endTime = null;
        this.timerExpired = false;
      }
    }
  }
};
</script>

<style scoped>
.display-name-section {
  padding: 12px 16px 8px 16px;
  background-color: #fafafa;
  border-bottom: 1px solid #e0e0e0;
}

.display-name-text {
  font-size: 0.9rem;
  color: #555;
}

.timer-container {
  margin-top: 12px;
  padding: 6px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.timer-content {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 6px;
}

.timer-text {
  font-size: 0.9rem;
  color: #333;
}
</style>